import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  getUserSubscriptionSchedulesByRestaurantIdAndDate,
  formatDateForAPI 
} from "../../../../service/subscriptionSchedule";

/**
 * Custom hook for managing map data and state
 */
export const useMapData = (restaurantId, selectedDate, language) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("all");

  // Get unique time ranges from schedules
  const getUniqueTimeRanges = useMemo(() => {
    const timeRanges = new Set();
    schedules.forEach((schedule) => {
      if (schedule.timeToServeFrom && schedule.timeToServeTo) {
        const timeRange = `${schedule.timeToServeFrom}:00 - ${schedule.timeToServeTo}:00`;
        timeRanges.add(timeRange);
      }
    });
    return Array.from(timeRanges).sort();
  }, [schedules]);

  // Filter schedules based on selected time filter
  const filteredSchedules = useMemo(() => {
    if (selectedTimeFilter === "all") {
      return schedules;
    }

    return schedules.filter((schedule) => {
      if (!schedule.timeToServeFrom || !schedule.timeToServeTo) {
        return selectedTimeFilter === "no-time";
      }
      const timeRange = `${schedule.timeToServeFrom}:00 - ${schedule.timeToServeTo}:00`;
      return timeRange === selectedTimeFilter;
    });
  }, [schedules, selectedTimeFilter]);

  // Load schedule data
  const loadSchedules = useCallback(async () => {
    if (!restaurantId) return;

    try {
      setLoading(true);
      setError(null);

      const date = selectedDate
        ? formatDateForAPI(selectedDate)
        : formatDateForAPI(new Date());
      const data = await getUserSubscriptionSchedulesByRestaurantIdAndDate(
        restaurantId,
        date,
        language
      );

      setSchedules(data);
    } catch (err) {
      console.error("Error loading subscription schedules:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, selectedDate, language]);

  // Load schedules when component mounts or dependencies change
  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  return {
    schedules,
    loading,
    error,
    selectedTimeFilter,
    setSelectedTimeFilter,
    getUniqueTimeRanges,
    filteredSchedules,
    loadSchedules,
  };
};
